import { useState, useEffect } from "react";
import { AlphaSlider, ColorSwatch, HueSlider, SaturationPicker } from "./ColorPicker";
import { hslToHex, hexToRgba } from "Vendor/Utils/Colors";
import { useDispatch, useSelector } from "react-redux";
import { selectSingleSelectedItem, selectGroupSelectedItems } from "Vendor/Store/studio/selectors";
import { ActiveLayoutItem } from "Vendor/Store/studio/initialState";
import { GridItemStyle } from "Vendor/Utils/StudioTypes";
import { AppDispatch, RootState } from "Vendor/Store/store";
import { updateItem } from "Vendor/Store/studio";
import { IconType } from "Vendor/Store/studio/activeToolSlice";
import DraggableItem from "../Canvas/Items/_DraggableItem";

interface ColorItem {
  name: string;
  value: string;
}

interface ColorPaletteProps {
  defaultColors?: ColorItem[];
  recentColors?: ColorItem[];
  field?: IconType | null;
  draggable?: boolean;
}

const ColorPalette: React.FC<ColorPaletteProps> = ({
  defaultColors = [],
  recentColors = [],
  field = "backgroundColor",
  draggable = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const selectedItem = useSelector(selectSingleSelectedItem);
  const selectedGroupItems = useSelector(selectGroupSelectedItems);
  const activePageId = useSelector((state: RootState) => state.studio.present.activePageId);

  const [localItem, setLocalItem] = useState<ActiveLayoutItem | null>(null);

  // Sliders state
  const [hue, setHue] = useState(280);
  const [saturation, setSaturation] = useState(80);
  const [brightness, setBrightness] = useState(60);
  const [alpha, setAlpha] = useState(1);

  // Selected color state
  const [selectedColor, setSelectedColor] = useState<string | undefined>(undefined);

  const hasGroup = selectedGroupItems && selectedGroupItems.length > 0;

  // Initialize localItem and selectedColor
  useEffect(() => {
    if (!selectedItem && !hasGroup) {
      setLocalItem(null);
      setSelectedColor(undefined);
      return;
    }

    const targetItem = hasGroup ? selectedGroupItems[0] : selectedItem;
    setLocalItem(targetItem);

    if (!selectedColor) {
      const initialColor =
        (field === "backgroundColor" && targetItem?.styles.backgroundColor) ||
        (field === "outlineColor" && targetItem?.styles.outlineColor);

      if (initialColor) setSelectedColor(initialColor);
    }
  }, [selectedItem, selectedGroupItems, field]);

  const updateStyles = (item: ActiveLayoutItem, styles: Partial<GridItemStyle>) => ({
    ...item,
    styles: { ...item.styles, ...styles },
  });

  const handleStyleChange = (
    style: keyof GridItemStyle,
    value: string | number | undefined
  ) => {
    if (!activePageId) return;

    if (hasGroup) {
      selectedGroupItems.forEach((item) => {
        dispatch(
          updateItem({
            pageId: activePageId,
            updatedItem: updateStyles(item, { [style]: value }),
          })
        );
      });
    } else if (localItem) {
      const updatedItem = updateStyles(localItem, { [style]: value });
      setLocalItem(updatedItem);
      dispatch(updateItem({ pageId: activePageId, updatedItem }));
    }
  };

  const handleStyleChangeMultiple = (styles: Partial<GridItemStyle>) => {
    if (!activePageId) return;

    if (hasGroup) {
      selectedGroupItems.forEach((item) => {
        const updatedItem = {
          ...item,
          styles: {
            ...item.styles,
            ...styles,
          },
        };

        dispatch(updateItem({ pageId: activePageId, updatedItem }));
      });
    } else if (localItem) {
      const updatedItem = {
        ...localItem,
        styles: {
          ...localItem.styles,
          ...styles,
        },
      };

      setLocalItem(updatedItem);
      dispatch(updateItem({ pageId: activePageId, updatedItem }));
    }
  };

  const getColorWithAlpha = () => {
    if (!selectedColor) return undefined;

    return hexToRgba(selectedColor, alpha);
  };

  const handleColorChange = (color: string) => {
    setSelectedColor(color);

    if (!localItem) return;

    const colorWithAlpha = hexToRgba(color, alpha);

    if (field === "backgroundColor") {
      handleStyleChange("backgroundColor", colorWithAlpha);
    } else if (field === "outlineColor") {
      handleStyleChangeMultiple({
        outlineColor: colorWithAlpha,
        outlineWidth: String(localItem.styles.outlineWidth || "0px"),
        outlineStyle: "solid",
      });
    }
  };

  const handleAlphaChange = (value: number) => {
    setAlpha(value);

    if (selectedColor) handleColorChange(selectedColor);
  };

  const handlePresetClick = (color: ColorItem) => handleColorChange(color.value);

  const handleSaturationChange = (sat: number, bright: number) => {
    setSaturation(sat);
    setBrightness(bright);
    const hex = hslToHex(hue, sat, bright);
    handleColorChange(hex);
  };

  const handleEyeDropper = async () => {
    if (!window.EyeDropper) return;
    try {
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      handleColorChange(result.sRGBHex);
    } catch {
      console.log("EyeDropper cancelled");
    }
  };

  const outlineWidthNumber = parseInt(String(localItem?.styles.outlineWidth || "0"), 10);

  return (
    <div className="picker-panel">
      {field === "outlineColor" && (
        <div className="stroke-section">
          <h3 className="stroke-section__label">Epaisseur trait</h3>
          <div className="stroke-section__controls">
            <input
              type="range"
              className="stroke-section__slider"
              min={0}
              max={100}
              value={outlineWidthNumber}
              onChange={(e) => handleStyleChange("outlineWidth", `${e.target.value}px`)}
            />
            <div className="stroke-section__value">
              {outlineWidthNumber}px <i className="fa-solid fa-caret-down" />
            </div>
          </div>
        </div>
      )}

      <div className="picker-header">
        <h2 className="picker-header__title">Couleur</h2>
        <button className="picker-header__eyedropper" onClick={handleEyeDropper}>
          <i className="fa-solid fa-eye-dropper" />
        </button>
      </div>

      <SaturationPicker
        hue={hue}
        saturation={saturation}
        brightness={brightness}
        onChange={handleSaturationChange}
      />

      <div className="sliders-section">
        <div className="sliders-container">
          <HueSlider hue={hue} onChange={setHue} />
          <AlphaSlider alpha={alpha} color={selectedColor || "#ffffff"} onChange={handleAlphaChange} />
        </div>

        <div className="color-preview" style={{ backgroundColor: getColorWithAlpha() }} />
      </div>

      {/* Default Colors */}
      {defaultColors.length > 0 && (
        <div className="swatches-section">
          <h3 className="section-title">Style par défaut</h3>
          <div className="swatches-grid">
            {defaultColors.map((item) =>
              draggable ? (
                <DraggableItem
                  key={item.value}
                  id={`color-${item.value}`}
                  type="COLOR"
                  value={item.value}
                  style={{ backgroundColor: item.value }}
                  label={""}
                />
              ) : (
                <ColorSwatch
                  key={item.value}
                  color={item.value}
                  isSelected={selectedColor === item.value}
                  onClick={() => handlePresetClick(item)}
                />
              )
            )}
          </div>
        </div>
      )}

      {/* Recent Colors */}
      {recentColors.length > 0 && (
        <div className="swatches-section">
          <h3 className="section-title">Récemment utilisé</h3>
          <div className="swatches-grid">
            {recentColors.map((item, index) => draggable ? (
              <DraggableItem
                  key={item.value}
                  id={`color-${item.value}`}
                  type="COLOR"
                  value={item.value}
                  style={{ backgroundColor: item.value }}
                  label={""}
                />
            ): (
              <ColorSwatch
                key={`${item.value}-${index}`}
                color={item.value}
                isSelected={selectedColor === item.value}
                onClick={() => handlePresetClick(item)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPalette;
